/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { type CreateModelRequest, type ModelType } from "@/api/admin-api"
import { ChevronDown, ChevronRight, Info } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
// Using native overflow instead of ScrollArea for better flex compatibility
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { useImageModelFamilies } from "../hooks/use-image-model-families"

// Model presets with official Azure OpenAI specifications
const MODEL_PRESETS = {
  // GPT-5.2 Series
  "gpt-5.2": {
    group: "GPT-5.2 Series",
    display_name: "GPT-5.2",
    description: "Latest reasoning model with advanced capabilities",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
      supports_streaming: true,
    },
  },
  "gpt-5.2-chat": {
    group: "GPT-5.2 Series",
    display_name: "GPT-5.2 Chat",
    description: "Chat-optimized variant (Preview)",
    model_type: "chat" as ModelType,
    input_token_limit: 111616,
    output_token_limit: 16384,
    feature_flags: {
      supports_structured_outputs: true,
      supports_tool_calls: true,
      supports_streaming: true,
      supports_temperature: true,
    },
  },
  "gpt-5.2-codex": {
    group: "GPT-5.2 Series",
    display_name: "GPT-5.2 Codex",
    description: "Optimized for Codex CLI and coding",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  // GPT-5.1 Series
  "gpt-5.1": {
    group: "GPT-5.1 Series",
    display_name: "GPT-5.1",
    description: "Advanced reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
      supports_streaming: true,
    },
  },
  "gpt-5.1-chat": {
    group: "GPT-5.1 Series",
    display_name: "GPT-5.1 Chat",
    description: "Chat-optimized variant (Preview)",
    model_type: "chat" as ModelType,
    input_token_limit: 111616,
    output_token_limit: 16384,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_tool_calls: true,
      supports_streaming: true,
    },
  },
  "gpt-5.1-codex": {
    group: "GPT-5.1 Series",
    display_name: "GPT-5.1 Codex",
    description: "Responses API only, optimized for coding",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "gpt-5.1-codex-mini": {
    group: "GPT-5.1 Series",
    display_name: "GPT-5.1 Codex Mini",
    description: "Smaller, faster coding model",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "gpt-5.1-codex-max": {
    group: "GPT-5.1 Series",
    display_name: "GPT-5.1 Codex Max",
    description: "Extended reasoning capabilities",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_reasoning_efforts: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  // GPT-5 Series
  "gpt-5": {
    group: "GPT-5 Series",
    display_name: "GPT-5",
    description: "Powerful reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
      supports_streaming: true,
    },
  },
  "gpt-5-mini": {
    group: "GPT-5 Series",
    display_name: "GPT-5 Mini",
    description: "Smaller, efficient reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "gpt-5-nano": {
    group: "GPT-5 Series",
    display_name: "GPT-5 Nano",
    description: "Fastest, most efficient GPT-5 variant",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "gpt-5-chat": {
    group: "GPT-5 Series",
    display_name: "GPT-5 Chat",
    description: "Chat-optimized (Preview)",
    model_type: "chat" as ModelType,
    input_token_limit: 128000,
    output_token_limit: 16384,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
    },
  },
  "gpt-5-codex": {
    group: "GPT-5 Series",
    display_name: "GPT-5 Codex",
    description: "Optimized for coding tasks",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "gpt-5-pro": {
    group: "GPT-5 Series",
    display_name: "GPT-5 Pro",
    description: "Premium reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 272000,
    output_token_limit: 128000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  // GPT-4.1 Series
  "gpt-4.1": {
    group: "GPT-4.1 Series",
    display_name: "GPT-4.1",
    description: "1M token context window model",
    model_type: "chat" as ModelType,
    input_token_limit: 1047576,
    output_token_limit: 32768,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
      supports_structured_outputs: true,
    },
  },
  "gpt-4.1-mini": {
    group: "GPT-4.1 Series",
    display_name: "GPT-4.1 Mini",
    description: "Smaller, efficient variant",
    model_type: "chat" as ModelType,
    input_token_limit: 1047576,
    output_token_limit: 32768,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
      supports_structured_outputs: true,
    },
  },
  "gpt-4.1-nano": {
    group: "GPT-4.1 Series",
    display_name: "GPT-4.1 Nano",
    description: "Fastest 4.1 variant",
    model_type: "chat" as ModelType,
    input_token_limit: 1047576,
    output_token_limit: 32768,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
      supports_structured_outputs: true,
    },
  },
  // GPT-4o Series
  "gpt-4o": {
    group: "GPT-4o Series",
    display_name: "GPT-4o",
    description: "Multimodal omni model",
    model_type: "chat" as ModelType,
    input_token_limit: 128000,
    output_token_limit: 16384,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
      supports_structured_outputs: true,
    },
  },
  "gpt-4o-mini": {
    group: "GPT-4o Series",
    display_name: "GPT-4o Mini",
    description: "Fast, cost-effective multimodal",
    model_type: "chat" as ModelType,
    input_token_limit: 128000,
    output_token_limit: 16384,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
    },
  },
  "gpt-4-turbo": {
    group: "GPT-4o Series",
    display_name: "GPT-4 Turbo",
    description: "GPT-4 Turbo with Vision",
    model_type: "chat" as ModelType,
    input_token_limit: 128000,
    output_token_limit: 4096,
    feature_flags: {
      supports_images: true,
      supports_streaming: true,
      supports_temperature: true,
      supports_system_message: true,
      supports_tool_calls: true,
    },
  },
  // Reasoning Models (o-series)
  o1: {
    group: "Reasoning (o-series)",
    display_name: "o1",
    description: "Advanced reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 200000,
    output_token_limit: 100000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  o3: {
    group: "Reasoning (o-series)",
    display_name: "o3",
    description: "Enhanced reasoning with parallel tools",
    model_type: "chat" as ModelType,
    input_token_limit: 200000,
    output_token_limit: 100000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  "o3-mini": {
    group: "Reasoning (o-series)",
    display_name: "o3 Mini",
    description: "Efficient reasoning, text-only",
    model_type: "chat" as ModelType,
    input_token_limit: 200000,
    output_token_limit: 100000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_tool_calls: true,
    },
  },
  "o4-mini": {
    group: "Reasoning (o-series)",
    display_name: "o4 Mini",
    description: "Latest efficient reasoning model",
    model_type: "chat" as ModelType,
    input_token_limit: 200000,
    output_token_limit: 100000,
    feature_flags: {
      is_reasoning_model: true,
      supports_structured_outputs: true,
      supports_images: true,
      supports_tool_calls: true,
    },
  },
  // Embedding Models
  "text-embedding-3-large": {
    group: "Embedding Models",
    display_name: "text-embedding-3-large",
    description: "Latest, most capable embedding model",
    model_type: "embedding" as ModelType,
    dimensions: 3072,
  },
  "text-embedding-3-small": {
    group: "Embedding Models",
    display_name: "text-embedding-3-small",
    description: "Efficient embedding model",
    model_type: "embedding" as ModelType,
    dimensions: 1536,
  },
  "text-embedding-ada-002": {
    group: "Embedding Models",
    display_name: "text-embedding-ada-002",
    description: "Legacy embedding model",
    model_type: "embedding" as ModelType,
    dimensions: 1536,
  },
  // Specialized
  "gpt-4o-realtime-preview": {
    group: "Specialized",
    display_name: "GPT-4o Realtime",
    description: "Real-time audio processing",
    model_type: "specialized" as ModelType,
    type: "realtime" as const,
    api_version: "2024-12-17",
  },
  // Image models. `model` is the Azure image *family* id the backend resolves
  // capabilities from; the registry id stays hyphenated (it must match
  // /^[a-z0-9_-]+$/, which the dotted family ids do not). `deployment` has to
  // match the Azure deployment exactly — dots and all — so both are pinned per
  // preset instead of being derived from the display name.
  "gpt-image-2-5-flare": {
    group: "Specialized",
    display_name: "GPT-Image 2.5 Flare",
    description: "Fast, high-quality everyday image generation",
    model_type: "specialized" as ModelType,
    type: "image" as const,
    model: "gpt-image-2.5-flare",
    id: "gpt-image-2-5-flare_global",
    deployment: "gpt-image-2.5-flare_global",
    api_version: "2025-04-01-preview",
  },
  "gpt-image-2-5-sunburst": {
    group: "Specialized",
    display_name: "GPT-Image 2.5 Sunburst",
    description: "Most capable; best editing precision",
    model_type: "specialized" as ModelType,
    type: "image" as const,
    model: "gpt-image-2.5-sunburst",
    id: "gpt-image-2-5-sunburst_global",
    deployment: "gpt-image-2.5-sunburst_global",
    api_version: "2025-04-01-preview",
  },
  "gpt-image-2": {
    group: "Specialized",
    display_name: "GPT-Image 2",
    description: "Previous-generation image model",
    model_type: "specialized" as ModelType,
    type: "image" as const,
    model: "gpt-image-2",
    id: "gpt-image-2_global",
    deployment: "gpt-image-2_global",
    api_version: "2025-04-01-preview",
  },
  "gpt-image-1-5": {
    group: "Specialized",
    display_name: "GPT-Image 1.5",
    description: "Legacy image model",
    model_type: "specialized" as ModelType,
    type: "image" as const,
    model: "gpt-image-1.5",
    id: "gpt-image-1-5_eu",
    deployment: "gpt-image-1.5_eu",
    api_version: "2025-04-01-preview",
  },
  whisper: {
    group: "Specialized",
    display_name: "Whisper",
    description: "Speech-to-text transcription",
    model_type: "specialized" as ModelType,
    type: "audio" as const,
  },
}

// Group presets by their group field
const PRESET_GROUPS: Record<
  string,
  Array<{ key: string } & (typeof MODEL_PRESETS)[keyof typeof MODEL_PRESETS]>
> = {}
for (const [key, preset] of Object.entries(MODEL_PRESETS)) {
  const group = preset.group
  if (!(group in PRESET_GROUPS)) {
    PRESET_GROUPS[group] = []
  }
  PRESET_GROUPS[group].push({ key, ...preset })
}

// Order of groups
const GROUP_ORDER = [
  "GPT-5.2 Series",
  "GPT-5.1 Series",
  "GPT-5 Series",
  "GPT-4.1 Series",
  "GPT-4o Series",
  "Reasoning (o-series)",
  "Embedding Models",
  "Specialized",
]

export type AddModelFormData = {
  id: string
  display_name: string
  description: string
  model_type: ModelType
  service_name: string
  deployment: string
  service_type: "azure" | "openai"
  enabled: boolean
  // Chat specific
  input_token_limit: number
  output_token_limit: number
  feature_flags: {
    supports_images: boolean
    supports_streaming: boolean
    supports_temperature: boolean
    is_reasoning_model: boolean
    supports_thinking_streaming: boolean
    supports_system_message: boolean
    supports_max_completion_tokens: boolean
    supports_reasoning_efforts: boolean
    supports_tool_calls: boolean
    supports_structured_outputs: boolean
  }
  // Embedding specific
  dimensions: number
  // Specialized specific
  type: "realtime" | "image" | "video" | "audio" | ""
  api_version: string
  /** Image-family id; only sent for `type === "image"` entries. */
  model: string
}

type ExistingModel = {
  id: string
  service_name?: string
}

type AddModelDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: CreateModelRequest) => Promise<void>
  isPending: boolean
  existingModels?: ExistingModel[]
}

const DEFAULT_FEATURE_FLAGS: AddModelFormData["feature_flags"] = {
  supports_images: false,
  supports_streaming: true,
  supports_temperature: true,
  is_reasoning_model: false,
  supports_thinking_streaming: false,
  supports_system_message: true,
  supports_max_completion_tokens: false,
  supports_reasoning_efforts: false,
  supports_tool_calls: true,
  supports_structured_outputs: false,
}

const getDefaultFormData = (): AddModelFormData => ({
  id: "",
  display_name: "",
  description: "",
  model_type: "chat",
  service_name: "",
  deployment: "",
  service_type: "azure",
  enabled: true,
  input_token_limit: 128000,
  output_token_limit: 16384,
  feature_flags: { ...DEFAULT_FEATURE_FLAGS },
  dimensions: 1536,
  type: "",
  api_version: "",
  model: "",
})

export function AddModelDialog({
  isOpen,
  onOpenChange,
  onSubmit,
  isPending,
  existingModels = [],
}: AddModelDialogProps) {
  const { t } = useTranslation()

  // Extract default service name from existing models
  const defaultServiceName = useMemo(() => {
    const serviceNames = existingModels
      .map((m) => m.service_name)
      .filter((s): s is string => Boolean(s))
    return serviceNames[0] ?? ""
  }, [existingModels])

  const [formData, setFormData] = useState<AddModelFormData>(getDefaultFormData())
  const [errors, setErrors] = useState<Partial<Record<keyof AddModelFormData, string>>>({})
  const [idManuallyEdited, setIdManuallyEdited] = useState(false)
  const [deploymentManuallyEdited, setDeploymentManuallyEdited] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<string>("")

  // Only fetched once an image entry is actually being configured.
  const { families: imageFamilies } = useImageModelFamilies(
    isOpen && formData.model_type === "specialized" && formData.type === "image"
  )

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(getDefaultFormData())
      setErrors({})
      setIdManuallyEdited(false)
      setDeploymentManuallyEdited(false)
      setAdvancedOpen(false)
      setSelectedPreset("")
    }
  }, [isOpen])

  // Pre-fill service name when dialog opens
  useEffect(() => {
    if (isOpen && defaultServiceName && !formData.service_name) {
      setFormData((prev) => ({ ...prev, service_name: defaultServiceName }))
    }
  }, [isOpen, defaultServiceName, formData.service_name])

  // Auto-generate ID and deployment from display name
  // Convention: ID = deployment = {name-with-hyphens}_eu
  const handleDisplayNameChange = (value: string) => {
    const baseName = value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
    const generatedId = baseName ? `${baseName}_eu` : ""

    setFormData((prev) => {
      const newId = idManuallyEdited ? prev.id : generatedId
      // Deployment = ID by default (both include _eu suffix)
      const deployment = deploymentManuallyEdited ? prev.deployment : newId
      return {
        ...prev,
        display_name: value,
        id: newId,
        deployment,
      }
    })
  }

  // Handle preset selection
  const handlePresetChange = (presetKey: string) => {
    setSelectedPreset(presetKey)

    if (presetKey === "custom") {
      // Reset to defaults for custom, but keep service name
      setFormData(() => ({
        ...getDefaultFormData(),
        service_name: defaultServiceName,
      }))
      setIdManuallyEdited(false)
      setDeploymentManuallyEdited(false)
      return
    }

    const preset = MODEL_PRESETS[presetKey as keyof typeof MODEL_PRESETS] as
      | (typeof MODEL_PRESETS)[keyof typeof MODEL_PRESETS]
      | undefined
    if (!preset) return

    const baseName = preset.display_name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
    const generatedId = `${baseName}_eu`

    // A preset may pin its own id and/or deployment (image models: the Azure
    // deployment keeps the dots the registry id may not, and they are global
    // rather than EU). Anything pinned is treated as already hand-set, or the
    // next display-name keystroke would regenerate `${baseName}_eu` over it.
    const presetId = "id" in preset ? preset.id : undefined
    const presetDeployment = "deployment" in preset ? preset.deployment : undefined
    const resolvedId = presetId ?? generatedId

    setFormData((prev) => ({
      ...prev,
      id: resolvedId,
      display_name: preset.display_name,
      description: preset.description,
      model_type: preset.model_type,
      // Deployment = ID unless the preset pins a different Azure name
      deployment: presetDeployment ?? resolvedId,
      // Keep service name from existing models
      service_name: prev.service_name || defaultServiceName,
      input_token_limit:
        "input_token_limit" in preset ? preset.input_token_limit : prev.input_token_limit,
      output_token_limit:
        "output_token_limit" in preset ? preset.output_token_limit : prev.output_token_limit,
      feature_flags:
        "feature_flags" in preset
          ? { ...DEFAULT_FEATURE_FLAGS, ...preset.feature_flags }
          : { ...DEFAULT_FEATURE_FLAGS },
      dimensions: "dimensions" in preset ? preset.dimensions : prev.dimensions,
      type: "type" in preset ? preset.type : "",
      api_version: "api_version" in preset ? preset.api_version : "",
      model: "model" in preset ? preset.model : "",
    }))
    setIdManuallyEdited(presetId !== undefined)
    setDeploymentManuallyEdited(presetDeployment !== undefined)
  }

  const validate = (): boolean => {
    const newErrors: typeof errors = {}

    if (!formData.id.trim()) {
      newErrors.id = t("adminDashboard.models.addDialog.errors.idRequired", "Model ID is required")
    } else if (!/^[a-z0-9_-]+$/.test(formData.id)) {
      newErrors.id = t(
        "adminDashboard.models.addDialog.errors.idInvalid",
        "ID must contain only lowercase letters, numbers, hyphens, and underscores"
      )
    }

    if (!formData.display_name.trim()) {
      newErrors.display_name = t(
        "adminDashboard.models.addDialog.errors.displayNameRequired",
        "Display name is required"
      )
    }

    if (!formData.service_name.trim()) {
      newErrors.service_name = t(
        "adminDashboard.models.addDialog.errors.serviceNameRequired",
        "Service name is required"
      )
    }

    if (!formData.deployment.trim()) {
      newErrors.deployment = t(
        "adminDashboard.models.addDialog.errors.deploymentRequired",
        "Deployment name is required"
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!validate()) return

    // Build the request object
    const request: CreateModelRequest = {
      id: formData.id,
      display_name: formData.display_name,
      description: formData.description,
      model_type: formData.model_type,
      service_name: formData.service_name,
      deployment: formData.deployment,
      service_type: formData.service_type,
      enabled: formData.enabled,
    }

    // Add type-specific fields
    if (formData.model_type === "chat") {
      request.input_token_limit = formData.input_token_limit
      request.output_token_limit = formData.output_token_limit
      request.feature_flags = formData.feature_flags
    } else if (formData.model_type === "embedding") {
      request.dimensions = formData.dimensions
    } else {
      // specialized
      request.type = formData.type || undefined
      request.api_version = formData.api_version || undefined
      // Only image entries carry a family id; switching the specialized type
      // after picking a preset must not leave a stale `model` in the payload.
      if (formData.type === "image" && formData.model) {
        request.model = formData.model
      }
    }

    await onSubmit(request)
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t("adminDashboard.models.addDialog.title", "Add Model")}</DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.models.addDialog.description",
              "Add a new AI model by selecting a preset or configuring manually."
            )}
          </DialogDescription>
        </DialogHeader>

        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-6 px-1 py-4">
              {/* Preset Selector */}
              <div className="space-y-2">
                <Label htmlFor="preset">
                  {t("adminDashboard.models.addDialog.preset", "Start from preset")}
                </Label>
                <Select value={selectedPreset} onValueChange={handlePresetChange}>
                  <SelectTrigger id="preset">
                    <SelectValue
                      placeholder={t(
                        "adminDashboard.models.addDialog.presetPlaceholder",
                        "Select a model preset or configure manually"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">
                      {t("adminDashboard.models.addDialog.customModel", "Custom Model")}
                    </SelectItem>
                    {GROUP_ORDER.map((groupName) => {
                      const presets = PRESET_GROUPS[groupName] as
                        | (typeof PRESET_GROUPS)[string]
                        | undefined
                      if (!presets?.length) return null
                      return (
                        <SelectGroup key={groupName}>
                          <SelectLabel>{groupName}</SelectLabel>
                          {presets.map((preset) => (
                            <SelectItem key={preset.key} value={preset.key}>
                              {preset.display_name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      )
                    })}
                  </SelectContent>
                </Select>
                {selectedPreset && selectedPreset !== "custom" && (
                  <p className="text-muted-foreground flex items-center gap-1 text-sm">
                    <Info className="size-3" />
                    {t(
                      "adminDashboard.models.addDialog.presetHint",
                      "Preset values applied. You still need to provide your Azure service name and deployment."
                    )}
                  </p>
                )}
              </div>

              {/* Display Name */}
              <div className="space-y-2">
                <Label htmlFor="display_name">
                  {t("adminDashboard.models.addDialog.displayName", "Display Name")} *
                </Label>
                <Input
                  id="display_name"
                  value={formData.display_name}
                  placeholder={t(
                    "adminDashboard.models.addDialog.displayNamePlaceholder",
                    "e.g., GPT-4o Production"
                  )}
                  onChange={(e) => handleDisplayNameChange(e.target.value)}
                />
                {errors.display_name && (
                  <p className="text-destructive text-sm">{errors.display_name}</p>
                )}
              </div>

              {/* Model ID */}
              <div className="space-y-2">
                <Label htmlFor="id">
                  {t("adminDashboard.models.addDialog.modelId", "Model ID")} *
                </Label>
                <Input
                  id="id"
                  value={formData.id}
                  placeholder={t(
                    "adminDashboard.models.addDialog.modelIdPlaceholder",
                    "e.g., gpt-4o-production"
                  )}
                  onChange={(e) => {
                    setIdManuallyEdited(true)
                    setFormData((prev) => ({ ...prev, id: e.target.value }))
                  }}
                />
                {errors.id && <p className="text-destructive text-sm">{errors.id}</p>}
                <p className="text-muted-foreground text-xs">
                  {t(
                    "adminDashboard.models.addDialog.modelIdHint",
                    "Unique identifier. Format: name-with-hyphens_region (e.g., gpt-4o_eu)"
                  )}
                </p>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">
                  {t("adminDashboard.models.addDialog.descriptionLabel", "Description")}
                </Label>
                <Textarea
                  id="description"
                  rows={2}
                  value={formData.description}
                  placeholder={t(
                    "adminDashboard.models.addDialog.descriptionPlaceholder",
                    "Optional description for this model"
                  )}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>

              {/* Model Type */}
              <div className="space-y-2">
                <Label htmlFor="model_type">
                  {t("adminDashboard.models.addDialog.modelType", "Model Type")} *
                </Label>
                <Select
                  value={formData.model_type}
                  onValueChange={(value: ModelType) =>
                    setFormData((prev) => ({ ...prev, model_type: value }))
                  }
                >
                  <SelectTrigger id="model_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chat">
                      {t("adminDashboard.models.addDialog.typeChat", "Chat")}
                    </SelectItem>
                    <SelectItem value="embedding">
                      {t("adminDashboard.models.addDialog.typeEmbedding", "Embedding")}
                    </SelectItem>
                    <SelectItem value="specialized">
                      {t("adminDashboard.models.addDialog.typeSpecialized", "Specialized")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Azure Configuration */}
              <div className="rounded-lg border p-4">
                <h4 className="mb-4 font-medium">
                  {t("adminDashboard.models.addDialog.azureConfig", "Azure OpenAI Configuration")}
                </h4>

                <div className="space-y-4">
                  {/* Service Name */}
                  <div className="space-y-2">
                    <Label htmlFor="service_name">
                      {t("adminDashboard.models.addDialog.serviceName", "Service Name")} *
                    </Label>
                    <Input
                      id="service_name"
                      value={formData.service_name}
                      placeholder={t(
                        "adminDashboard.models.addDialog.serviceNamePlaceholder",
                        "e.g., my-openai-resource"
                      )}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, service_name: e.target.value }))
                      }
                    />
                    {errors.service_name && (
                      <p className="text-destructive text-sm">{errors.service_name}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {t(
                        "adminDashboard.models.addDialog.serviceNameHint",
                        "The Azure OpenAI resource name (from Azure portal)"
                      )}
                    </p>
                  </div>

                  {/* Deployment */}
                  <div className="space-y-2">
                    <Label htmlFor="deployment">
                      {t("adminDashboard.models.addDialog.deployment", "Deployment Name")} *
                    </Label>
                    <Input
                      id="deployment"
                      value={formData.deployment}
                      placeholder={t(
                        "adminDashboard.models.addDialog.deploymentPlaceholder",
                        "e.g., gpt-4o_eu"
                      )}
                      onChange={(e) => {
                        setDeploymentManuallyEdited(true)
                        setFormData((prev) => ({ ...prev, deployment: e.target.value }))
                      }}
                    />
                    {errors.deployment && (
                      <p className="text-destructive text-sm">{errors.deployment}</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      {formData.type === "image"
                        ? t("adminDashboard.models.addDialog.deploymentMustMatch", {
                            defaultValue:
                              "Must match the Azure deployment name exactly (e.g. gpt-image-2.5-flare_global)",
                          })
                        : t(
                            "adminDashboard.models.addDialog.deploymentHint",
                            "Must match the deployment name in Azure exactly"
                          )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Type-specific fields */}
              {formData.model_type === "embedding" && (
                <div className="space-y-2">
                  <Label htmlFor="dimensions">
                    {t("adminDashboard.models.addDialog.dimensions", "Dimensions")}
                  </Label>
                  <Input
                    id="dimensions"
                    type="number"
                    value={formData.dimensions}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dimensions: parseInt(e.target.value) || 1536,
                      }))
                    }
                  />
                  <p className="text-muted-foreground text-xs">
                    {t(
                      "adminDashboard.models.addDialog.dimensionsHint",
                      "Vector size for embeddings. Common: 1536, 3072"
                    )}
                  </p>
                </div>
              )}

              {formData.model_type === "specialized" && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="specialized_type">
                      {t("adminDashboard.models.addDialog.specializedType", "Specialized Type")}
                    </Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value: "realtime" | "image" | "video" | "audio" | "") =>
                        setFormData((prev) => ({ ...prev, type: value }))
                      }
                    >
                      <SelectTrigger id="specialized_type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="realtime">Realtime</SelectItem>
                        <SelectItem value="image">Image</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="audio">Audio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="api_version">
                      {t("adminDashboard.models.addDialog.apiVersion", "API Version")}
                    </Label>
                    <Input
                      id="api_version"
                      placeholder="e.g., 2024-12-17"
                      value={formData.api_version}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, api_version: e.target.value }))
                      }
                    />
                  </div>
                  {formData.type === "image" && (
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="image_model_family">
                        {t("adminDashboard.models.addDialog.imageModel", "Model")}
                      </Label>
                      <Select
                        value={formData.model}
                        onValueChange={(value) =>
                          setFormData((prev) => ({ ...prev, model: value }))
                        }
                      >
                        <SelectTrigger id="image_model_family">
                          <SelectValue
                            placeholder={t(
                              "adminDashboard.models.addDialog.imageModelPlaceholder",
                              "Select the Azure image model family"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {imageFamilies.map((family) => (
                            <SelectItem key={family.id} value={family.id}>
                              <span>{family.label}</span>
                              <span className="text-muted-foreground ml-2 text-xs">
                                {family.tier === "fast"
                                  ? t("adminDashboard.imageGeneration.tierFast", "Fast")
                                  : t(
                                      "adminDashboard.imageGeneration.tierQuality",
                                      "Highest quality"
                                    )}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Advanced Settings (for chat models) */}
              {formData.model_type === "chat" && (
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <CollapsibleTrigger asChild>
                    <Button className="w-full justify-start p-0" type="button" variant="ghost">
                      {advancedOpen ? (
                        <ChevronDown className="mr-2 size-4" />
                      ) : (
                        <ChevronRight className="mr-2 size-4" />
                      )}
                      {t("adminDashboard.models.addDialog.advancedSettings", "Advanced Settings")}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">
                    {/* Token Limits */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="input_token_limit">
                          {t(
                            "adminDashboard.models.addDialog.inputTokenLimit",
                            "Input Token Limit"
                          )}
                        </Label>
                        <Input
                          id="input_token_limit"
                          type="number"
                          value={formData.input_token_limit}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              input_token_limit: parseInt(e.target.value) || 128000,
                            }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="output_token_limit">
                          {t(
                            "adminDashboard.models.addDialog.outputTokenLimit",
                            "Output Token Limit"
                          )}
                        </Label>
                        <Input
                          id="output_token_limit"
                          type="number"
                          value={formData.output_token_limit}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              output_token_limit: parseInt(e.target.value) || 16384,
                            }))
                          }
                        />
                      </div>
                    </div>

                    {/* Feature Flags */}
                    <div className="space-y-2">
                      <Label>
                        {t("adminDashboard.models.addDialog.featureFlags", "Feature Flags")}
                      </Label>
                      <div className="grid grid-cols-2 gap-2 rounded-lg border p-3">
                        {Object.entries(formData.feature_flags).map(([key, value]) => (
                          <div key={key} className="flex items-center space-x-2">
                            <Checkbox
                              checked={value}
                              id={`flag-${key}`}
                              onCheckedChange={(checked) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  feature_flags: {
                                    ...prev.feature_flags,
                                    [key]: checked === true,
                                  },
                                }))
                              }
                            />
                            <Label
                              className="cursor-pointer text-sm font-normal"
                              htmlFor={`flag-${key}`}
                            >
                              {key.replace(/_/g, " ").replace(/supports /i, "")}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 shrink-0 border-t pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button disabled={isPending} type="submit">
              {isPending && <Spinner className="mr-2 size-4" />}
              {t("adminDashboard.models.addDialog.submit", "Add Model")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
